const DAY_MS = 86_400_000;

export interface CalendarDay {
  iso: string;
  day: number;
  inMonth: boolean;
}

export function isIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(value)) return false;
  const parsed = toUtc(value);
  return !Number.isNaN(parsed) && fromUtc(parsed) === value;
}

function toUtc(iso: string): number {
  return Date.parse(`${iso}T00:00:00Z`);
}

function fromUtc(milliseconds: number): string {
  return new Date(milliseconds).toISOString().slice(0, 10);
}

export function addDays(iso: string, days: number): string {
  return fromUtc(toUtc(iso) + days * DAY_MS);
}

export function addMonths(iso: string, months: number): string {
  const [year, month, day] = iso.split('-').map(Number) as [number, number, number];
  const target = new Date(Date.UTC(year, month - 1 + months, 1));
  const lastDay = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate();
  target.setUTCDate(Math.min(day, lastDay));
  return fromUtc(target.getTime());
}

export function daysBetween(from: string, to: string): number {
  return Math.round((toUtc(to) - toUtc(from)) / DAY_MS);
}

export function mondayIndex(iso: string): number {
  return (new Date(toUtc(iso)).getUTCDay() + 6) % 7;
}

export function startOfMonth(iso: string): string {
  return `${iso.slice(0, 7)}-01`;
}

export function monthGrid(anyDayInMonth: string): readonly (readonly CalendarDay[])[] {
  const first = startOfMonth(anyDayInMonth);
  const month = first.slice(0, 7);
  const gridStart = addDays(first, -mondayIndex(first));
  const weeks: CalendarDay[][] = [];
  for (let week = 0; week < 6; week += 1) {
    const days: CalendarDay[] = [];
    for (let weekday = 0; weekday < 7; weekday += 1) {
      const iso = addDays(gridStart, week * 7 + weekday);
      days.push({ iso, day: Number(iso.slice(8, 10)), inMonth: iso.slice(0, 7) === month });
    }
    weeks.push(days);
  }
  return weeks;
}

const WEEKDAY_SHORT = ['seg.', 'ter.', 'qua.', 'qui.', 'sex.', 'sáb.', 'dom.'] as const;
const MONTH_SHORT = [
  'jan.',
  'fev.',
  'mar.',
  'abr.',
  'mai.',
  'jun.',
  'jul.',
  'ago.',
  'set.',
  'out.',
  'nov.',
  'dez.',
] as const;
const MONTH_LONG = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
] as const;

function parts(iso: string): { year: number; month: number; day: number } {
  const [year, month, day] = iso.split('-').map(Number) as [number, number, number];
  return { year, month, day };
}

export const WEEKDAY_LABELS = ['S', 'T', 'Q', 'Q', 'S', 'S', 'D'] as const;
export const WEEKDAY_NAMES = [
  'segunda-feira',
  'terça-feira',
  'quarta-feira',
  'quinta-feira',
  'sexta-feira',
  'sábado',
  'domingo',
] as const;

export function monthTitle(iso: string): string {
  const { year, month } = parts(iso);
  return capitalise(`${MONTH_LONG[month - 1] ?? ''} ${String(year)}`);
}

export function shortDate(iso: string): string {
  const { month, day } = parts(iso);
  return `${WEEKDAY_SHORT[mondayIndex(iso)] ?? ''}, ${String(day)} ${MONTH_SHORT[month - 1] ?? ''}`;
}

export function longDate(iso: string): string {
  const { year, month, day } = parts(iso);
  return `${WEEKDAY_NAMES[mondayIndex(iso)] ?? ''}, ${String(day)} de ${MONTH_LONG[month - 1] ?? ''} de ${String(year)}`;
}

export function numericDate(iso: string): string {
  const { year, month, day } = parts(iso);
  return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${String(year)}`;
}

export function relativeDayLabel(iso: string, today: string): string | null {
  const difference = daysBetween(today, iso);
  if (difference === 0) return 'Hoje';
  if (difference === 1) return 'Amanhã';
  if (difference === -1) return 'Ontem';
  return null;
}

export function friendlyDate(iso: string, today: string): string {
  const relative = relativeDayLabel(iso, today);
  const short = shortDate(iso);
  return relative === null ? capitalise(short) : `${relative}, ${short}`;
}

export function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function clampDate(iso: string, min: string | null, max: string | null): string {
  if (min !== null && iso < min) return min;
  if (max !== null && iso > max) return max;
  return iso;
}
